import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { BookOpen, Users, Music, Target, Calendar, Mail, MapPin, User, Award, Heart, Globe, Shield, Star, Clock, Coffee, TrendingUp, Sparkles, ChevronRight, MessageCircle, ChevronLeft, ChevronRight as ChevronRightIcon, Play, Users as UsersIcon, X, Maximize2, Download, Share2, ZoomIn, ZoomOut, RotateCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const EAJ = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    age: '',
    location: '',
    experience: ''
  })
  
  const [currentSlide, setCurrentSlide] = useState(0)
  const [selectedImage, setSelectedImage] = useState(null)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })
  const [isDownloading, setIsDownloading] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [showCopied, setShowCopied] = useState(false)
  
  const imageRef = useRef(null)
  const containerRef = useRef(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { t } = useTranslation()

  // Données de la galerie
  const galleryPhotos = [
    {
      id: 1,
      image: "/assets/images/photo11.jpg",
      thumbnail: "/assets/images/photo11.jpg",
      title: t('eaj.gallery.photos.training.title'),
      description: t('eaj.gallery.photos.training.description'),
      date: t('eaj.gallery.photos.training.date'),
      location: t('eaj.gallery.photos.training.location'),
      participants: "13",
      tags: [
        t('eaj.gallery.tags.training'),
        t('eaj.gallery.tags.teamwork'),
        t('eaj.gallery.tags.expertise')
      ],
      fallback: "photo-1547425260-76bcadfb4f2c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 2,
      image: "/assets/images/photo22.jpg",
      thumbnail: "/assets/images/photo22.jpg",
      title: t('eaj.gallery.photos.worship.title'),
      description: t('eaj.gallery.photos.worship.description'),
      date: t('eaj.gallery.photos.worship.date'),
      location: t('eaj.gallery.photos.worship.location'),
      participants: "15",
      tags: [
        t('eaj.gallery.tags.worship'),
        t('eaj.gallery.tags.communion'),
        t('eaj.gallery.tags.celebration')
      ],
      fallback: "photo-1511379938547-c1f69419868d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 3,
      image: "/assets/images/photo33.JPG",
      thumbnail: "/assets/images/photo33.JPG",
      title: t('eaj.gallery.photos.workshop.title'),
      description: t('eaj.gallery.photos.workshop.description'),
      date: t('eaj.gallery.photos.workshop.date'),
      location: t('eaj.gallery.photos.workshop.location'),
      participants: "18",
      tags: [
        t('eaj.gallery.tags.practice'),
        t('eaj.gallery.tags.workshop'),
        t('eaj.gallery.tags.interaction')
      ],
      fallback: "photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 4,
      image: "/assets/images/photo44.jpg",
      thumbnail: "/assets/images/photo44.jpg",
      title: t('eaj.gallery.photos.worship2.title'),
      description: t('eaj.gallery.photos.worship2.description'),
      date: t('eaj.gallery.photos.worship2.date'),
      location: t('eaj.gallery.photos.worship2.location'),
      participants: "15",
      tags: [
        t('eaj.gallery.tags.worship'),
        t('eaj.gallery.tags.communion'),
        t('eaj.gallery.tags.celebration')
      ],
      fallback: "photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 5,
      image: "/assets/images/photo55.jpg",
      thumbnail: "/assets/images/photo55.jpg",
      title: t('eaj.gallery.photos.training2.title'),
      description: t('eaj.gallery.photos.training2.description'),
      date: t('eaj.gallery.photos.training2.date'),
      location: t('eaj.gallery.photos.training2.location'),
      participants: "13",
      tags: [
        t('eaj.gallery.tags.training'),
        t('eaj.gallery.tags.teamwork'),
        t('eaj.gallery.tags.expertise')
      ],
      fallback: "photo-1511795409834-ef04bbd61622?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80"
    }
  ]

  // Navigation automatique du slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % galleryPhotos.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [galleryPhotos.length])

  // Gestion de la touche Échap pour fermer la lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isLightboxOpen) {
        closeLightbox()
      }
      if (e.key === 'ArrowRight' && isLightboxOpen && selectedImage) {
        handleLightboxNext()
      }
      if (e.key === 'ArrowLeft' && isLightboxOpen && selectedImage) {
        handleLightboxPrev()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isLightboxOpen, selectedImage])

  // Réinitialiser le zoom quand on change d'image
  useEffect(() => {
    if (isLightboxOpen) {
      resetZoom()
    }
  }, [selectedImage?.id])

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryPhotos.length)
  }

  const handlePrev = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryPhotos.length) % galleryPhotos.length)
  }

  const openLightbox = (photo) => {
    setSelectedImage(photo)
    setIsLightboxOpen(true)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setIsLightboxOpen(false)
    setZoomLevel(1)
    setPosition({ x: 0, y: 0 })
    setTimeout(() => setSelectedImage(null), 300)
    document.body.style.overflow = 'auto'
  }

  const handleLightboxNext = () => {
    const currentIndex = galleryPhotos.findIndex(img => img.id === selectedImage.id)
    const nextIndex = (currentIndex + 1) % galleryPhotos.length
    setSelectedImage(galleryPhotos[nextIndex])
  }

  const handleLightboxPrev = () => {
    const currentIndex = galleryPhotos.findIndex(img => img.id === selectedImage.id)
    const prevIndex = (currentIndex - 1 + galleryPhotos.length) % galleryPhotos.length
    setSelectedImage(galleryPhotos[prevIndex])
  }

  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3))
  }

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1))
  }

  const resetZoom = () => {
    setZoomLevel(1)
    setPosition({ x: 0, y: 0 })
  }

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true)
      setStartPosition({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
    }
  }

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPosition({
        x: e.clientX - startPosition.x,
        y: e.clientY - startPosition.y
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      if (e.deltaY < 0) {
        zoomIn()
      } else {
        zoomOut()
      }
    }
  }

  const handleDownload = async () => {
    if (!selectedImage) return
    
    setIsDownloading(true)
    try {
      const response = await fetch(selectedImage.image)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `eaj-${selectedImage.title.toLowerCase().replace(/\s+/g, '-')}.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      setTimeout(() => setIsDownloading(false), 1000)
    } catch (error) {
      console.error('Erreur de téléchargement:', error)
      setIsDownloading(false)
      alert(t('eaj.lightbox.downloadError'))
    }
  }

  const handleShare = async () => {
    if (!selectedImage) return
    
    setIsSharing(true)
    try {
      if (navigator.share) {
        await navigator.share({
          title: selectedImage.title,
          text: selectedImage.description,
          url: window.location.href,
        })
      } else {
        await navigator.clipboard.writeText(window.location.href)
        setShowCopied(true)
        setTimeout(() => setShowCopied(false), 2000)
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Erreur de partage:', error)
        await navigator.clipboard.writeText(window.location.href)
        setShowCopied(true)
        setTimeout(() => setShowCopied(false), 2000)
      }
    } finally {
      setIsSharing(false)
    }
  }

  const programFeatures = [
    {
      icon: <BookOpen className="w-10 h-10" />,
      title: t('eaj.program.leadership.title'),
      description: t('eaj.program.leadership.description'),
      stats: t('eaj.program.leadership.stats'),
      color: 'from-blue-600/90 to-blue-800/90'
    },
    {
      icon: <Music className="w-10 h-10" />,
      title: t('eaj.program.artistic.title'),
      description: t('eaj.program.artistic.description'),
      stats: t('eaj.program.artistic.stats'),
      color: 'from-purple-600/90 to-purple-800/90'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: t('eaj.program.community.title'),
      description: t('eaj.program.community.description'),
      stats: t('eaj.program.community.stats'),
      color: 'from-emerald-600/90 to-emerald-800/90'
    },
    {
      icon: <Target className="w-10 h-10" />,
      title: t('eaj.program.mission.title'),
      description: t('eaj.program.mission.description'),
      stats: t('eaj.program.mission.stats'),
      color: 'from-amber-600/90 to-amber-800/90'
    }
  ]

  const activities = [
    {
      title: t('eaj.activities.coffee.title'),
      frequency: t('eaj.activities.coffee.frequency'),
      time: t('eaj.activities.coffee.time'),
      description: t('eaj.activities.coffee.description'),
      details: t('eaj.activities.coffee.details'),
      icon: <Coffee className="w-8 h-8" />,
      bgColor: 'bg-gradient-to-br from-amber-900/20 to-amber-700/10',
      borderColor: 'border-amber-500/30',
      learnMoreLink: '/activities/cafe-theologique'
    },
    {
      title: t('eaj.activities.rehearsal.title'),
      frequency: t('eaj.activities.rehearsal.frequency'),
      time: t('eaj.activities.rehearsal.time'),
      description: t('eaj.activities.rehearsal.description'),
      details: t('eaj.activities.rehearsal.details'),
      icon: <Music className="w-8 h-8" />,
      bgColor: 'bg-gradient-to-br from-purple-900/20 to-purple-700/10',
      borderColor: 'border-purple-500/30',
      learnMoreLink: '/activities/labo-louange'
    },
    {
      title: t('eaj.activities.prayer.title'),
      frequency: t('eaj.activities.prayer.frequency'),
      time: t('eaj.activities.prayer.time'),
      description: t('eaj.activities.prayer.description'),
      details: t('eaj.activities.prayer.details'),
      icon: <Sparkles className="w-8 h-8" />,
      bgColor: 'bg-gradient-to-br from-emerald-900/20 to-emerald-700/10',
      borderColor: 'border-emerald-500/30',
      learnMoreLink: '/activities/retraite-silencieuse'
    },
    {
      title: t('eaj.activities.outreach.title'),
      frequency: t('eaj.activities.outreach.frequency'),
      time: t('eaj.activities.outreach.time'),
      description: t('eaj.activities.outreach.description'),
      details: t('eaj.activities.outreach.details'),
      icon: <Globe className="w-8 h-8" />,
      bgColor: 'bg-gradient-to-br from-blue-900/20 to-blue-700/10',
      borderColor: 'border-blue-500/30',
      learnMoreLink: '/activities/action-urbaine'
    }
  ]

  const testimonials = [
    {
      name: t('eaj.testimonials.patricia.name'),
      age: t('eaj.testimonials.patricia.age'),
      role: t('eaj.testimonials.patricia.role'),
      quote: t('eaj.testimonials.patricia.quote'),
      avatarColor: "from-blue-500 to-cyan-600",
      rating: 5
    },
    {
      name: t('eaj.testimonials.yan.name'),
      age: t('eaj.testimonials.yan.age'),
      role: t('eaj.testimonials.yan.role'),
      quote: t('eaj.testimonials.yan.quote'),
      avatarColor: "from-pink-500 to-rose-600",
      rating: 5
    },
    {
      name: t('eaj.testimonials.rachel.name'),
      age: t('eaj.testimonials.rachel.age'),
      role: t('eaj.testimonials.rachel.role'),
      quote: t('eaj.testimonials.rachel.quote'),
      avatarColor: "from-purple-500 to-violet-600",
      rating: 5
    }
  ]

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    setTimeout(() => {
      console.log('Form submitted:', formData)
      setIsSubmitting(false)
      alert(t('eaj.form.successMessage'))
      setFormData({
        name: '',
        email: '',
        age: '',
        location: '',
        experience: ''
      })
    }, 1500)
  }

  return (
    <div className="min-h-screen bg-black text-white pt-24">
      {/* Section Programme */}
      <section className="py-24 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center justify-center gap-3 mb-6 px-8 py-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-full border border-blue-500/30">
              <TrendingUp className="w-8 h-8 text-blue-400" />
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                {t('eaj.title')}
              </h2>
            </div>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t('eaj.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {programFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className={`relative overflow-hidden rounded-3xl p-8 border ${feature.borderColor || 'border-gray-800'} transition-all duration-500 group`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-10 group-hover:opacity-20 transition-opacity duration-500`}></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-white/5 to-transparent rounded-full -translate-y-16 translate-x-16"></div>
                
                <div className="relative z-10 text-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-2xl mb-6`}>
                      <div className="text-white">
                        {feature.icon}
                      </div>
                    </div>
                    
                    <div className="w-full">
                      <div className="flex flex-col items-center mb-4">
                        <h3 className="text-2xl font-bold text-white group-hover:text-yellow-600 transition-colors mb-2">
                          {feature.title}
                        </h3>
                        <span className="px-6 py-2 bg-white/10 text-white/80 rounded-full text-sm font-medium">
                          {feature.stats}
                        </span>
                      </div>
                      
                      <p className="text-gray-300 leading-relaxed mb-6 text-center">
                        {feature.description}
                      </p>
                      
                      <div className="flex items-center justify-center text-sm text-gray-400">
                        <Clock className="w-4 h-4 mr-2" />
                        <span>{t('eaj.program.included')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Galerie Photos */}
      <section className="py-24 bg-black relative overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('eaj.gallery.title')} <span className="text-yellow-600">{t('eaj.gallery.titleHighlight')}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-10">
              {t('eaj.gallery.subtitle')}
            </p>
            
            <div className="inline-flex items-center gap-2 bg-yellow-600/20 text-yellow-400 px-4 py-2 rounded-full text-sm border border-yellow-600/30">
              <span>📸</span>
              <span>{t('eaj.gallery.updated')}</span>
            </div>
          </motion.div>

          <div className="relative max-w-6xl mx-auto">
            <button 
              onClick={handlePrev}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-black/80 text-white rounded-full flex items-center justify-center hover:bg-black hover:scale-110 transition-all duration-300 shadow-2xl border border-gray-800"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            
            <button 
              onClick={handleNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-black/80 text-white rounded-full flex items-center justify-center hover:bg-black hover:scale-110 transition-all duration-300 shadow-2xl border border-gray-800"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>

            <div className="overflow-hidden rounded-3xl">
              <motion.div 
                className="flex"
                animate={{ x: `-${currentSlide * 100}%` }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {galleryPhotos.map((photo, index) => (
                  <div key={photo.id} className="w-full flex-shrink-0">
                    <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden">
                      <motion.img
                        src={photo.image}
                        alt={photo.title}
                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-700"
                        onClick={() => openLightbox(photo)}
                        initial={{ scale: 1.1 }}
                        whileInView={{ scale: 1 }}
                        transition={{ duration: 1.5 }}
                        onError={(e) => {
                          e.target.src = `https://images.unsplash.com/${photo.fallback}`
                        }}
                      />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                      
                      <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-white">
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <div className="flex flex-wrap gap-2 mb-4">
                            {photo.tags.map((tag, i) => (
                              <span key={i} className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                                {tag}
                              </span>
                            ))}
                          </div>
                          
                          <h3 className="text-3xl md:text-4xl font-bold mb-3">{photo.title}</h3>
                          <p className="text-gray-300 text-lg md:text-xl mb-6 max-w-2xl">{photo.description}</p>
                          
                          <div className="flex items-center gap-6 text-gray-400">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-5 h-5" />
                              <span>{photo.date}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-5 h-5" />
                              <span>{photo.location}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <UsersIcon className="w-5 h-5" />
                              <span>{photo.participants} {t('eaj.gallery.participants')}</span>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                      
                      <div className="absolute top-6 right-6 bg-black/80 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                        📸 {index + 1}/{galleryPhotos.length}
                      </div>
                      
                      <div className="absolute top-6 left-6 bg-black/80 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium opacity-0 hover:opacity-100 transition-opacity duration-300 cursor-pointer flex items-center gap-2"
                           onClick={() => openLightbox(photo)}>
                        <Maximize2 className="w-4 h-4" />
                        {t('eaj.gallery.enlarge')}
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
            
            <div className="flex justify-center gap-2 mt-8">
              {galleryPhotos.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    currentSlide === index 
                      ? 'bg-yellow-600 w-8' 
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                />
              ))}
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center mt-10"
            >
              <p className="text-gray-500 text-sm">
                👆 <strong>{t('eaj.gallery.navigation')}</strong> : {t('eaj.gallery.navigationHint')} • 
                <span className="text-yellow-500 ml-2">
                  {galleryPhotos[currentSlide].title}
                </span>
              </p>
            </motion.div>
          </div>

          <div className="mt-16">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              {t('eaj.gallery.relive')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-4xl mx-auto">
              {galleryPhotos.map((photo, index) => (
                <motion.div
                  key={photo.id}
                  whileHover={{ y: -5, scale: 1.05 }}
                  className={`relative aspect-square overflow-hidden rounded-xl cursor-pointer border-2 ${
                    currentSlide === index ? 'border-yellow-500' : 'border-transparent'
                  }`}
                  onClick={() => {
                    setCurrentSlide(index)
                    openLightbox(photo)
                  }}
                >
                  <img
                    src={photo.thumbnail}
                    alt={photo.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `/assets/images/photo1.jpg/${photo.fallback}`
                    }}
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Play className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-4">
                    <span className="text-white text-sm font-medium">{photo.title}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-600/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-600/20 to-transparent"></div>
      </section>

      {/* Lightbox avec zoom et boutons fonctionnels */}
      <AnimatePresence>
        {isLightboxOpen && selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
            onClick={closeLightbox}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onWheel={handleWheel}
            ref={containerRef}
          >
            <div className="relative w-full max-w-6xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              {/* Bouton fermer */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-50 w-12 h-12 bg-black/80 text-white rounded-full flex items-center justify-center hover:bg-black hover:scale-110 transition-all duration-300 shadow-2xl border border-gray-800"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Boutons navigation */}
              <button 
                onClick={handleLightboxPrev}
                className="absolute left-12 top-64 transform -translate-y-1/2 z-50 w-12 h-12 bg-black/80 text-white rounded-full flex items-center justify-center hover:bg-black hover:scale-110 transition-all duration-300 shadow-2xl border border-gray-800"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <button 
                onClick={handleLightboxNext}
                className="absolute right-12 top-64 transform -translate-y-1/2 z-50 w-12 h-12 bg-black/80 text-white rounded-full flex items-center justify-center hover:bg-black hover:scale-110 transition-all duration-300 shadow-2xl border border-gray-800"
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>

              {/* Contrôles de zoom */}
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 flex items-center gap-2 bg-black/80 backdrop-blur-sm rounded-full p-2 border border-gray-800">
                <button
                  onClick={zoomOut}
                  disabled={zoomLevel <= 1}
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${zoomLevel <= 1 ? 'text-gray-500' : 'text-white hover:bg-gray-800'}`}
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                
                <span className="text-white text-sm font-medium px-2">
                  {Math.round(zoomLevel * 100)}%
                </span>
                
                <button
                  onClick={zoomIn}
                  disabled={zoomLevel >= 3}
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${zoomLevel >= 3 ? 'text-gray-500' : 'text-white hover:bg-gray-800'}`}
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                
                <button
                  onClick={resetZoom}
                  disabled={zoomLevel === 1 && position.x === 0 && position.y === 0}
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${zoomLevel === 1 && position.x === 0 && position.y === 0 ? 'text-gray-500' : 'text-white hover:bg-gray-800'}`}
                >
                  <RotateCw className="w-5 h-5" />
                </button>
              </div>

              {/* Contenu de la lightbox */}
              <div className="flex flex-col lg:flex-row gap-8 h-full">
                {/* Zone d'image avec zoom */}
                <div 
                  className="flex-1 overflow-hidden rounded-2xl relative"
                  onMouseDown={handleMouseDown}
                  ref={imageRef}
                  style={{ 
                    cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    touchAction: 'none'
                  }}
                >
                  <motion.div
                    key={selectedImage.id}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ 
                      scale: zoomLevel,
                      opacity: 1,
                      x: position.x,
                      y: position.y
                    }}
                    transition={{ duration: 0.3 }}
                    className="w-full h-full flex items-center justify-center"
                    style={{ 
                      transformOrigin: 'center center',
                      willChange: 'transform'
                    }}
                  >
                    <img
                      src={selectedImage.image}
                      alt={selectedImage.title}
                      className="max-w-full max-h-[70vh] object-contain select-none"
                      draggable="false"
                      onError={(e) => {
                        e.target.src = `https://images.unsplash.com/${selectedImage.fallback}`
                      }}
                    />
                  </motion.div>
                  
                  {/* Indicateur de zoom */}
                  {zoomLevel > 1 && (
                    <div className="absolute bottom-4 right-4 bg-black/80 text-white px-3 py-2 rounded-full text-sm">
                      {t('eaj.lightbox.dragHint')}
                    </div>
                  )}
                </div>

                {/* Informations et boutons */}
                <div className="lg:w-96 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-800">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2">{selectedImage.title}</h3>
                      <p className="text-gray-300">{selectedImage.description}</p>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-yellow-600" />
                        <span className="text-gray-300">{selectedImage.date}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-yellow-600" />
                        <span className="text-gray-300">{selectedImage.location}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <UsersIcon className="w-5 h-5 text-yellow-600" />
                        <span className="text-gray-300">{selectedImage.participants} {t('eaj.gallery.participants')}</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      {selectedImage.tags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-yellow-600/20 text-yellow-400 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Boutons d'action */}
                    <div className="space-y-3 pt-4 border-t border-gray-800">
                      <button 
                        onClick={handleDownload}
                        disabled={isDownloading}
                        className="w-full flex items-center justify-center gap-3 py-3 bg-gradient-to-r from-yellow-600 to-amber-700 hover:from-yellow-700 hover:to-amber-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-300 font-medium"
                      >
                        {isDownloading ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>{t('eaj.lightbox.downloading')}</span>
                          </>
                        ) : (
                          <>
                            <Download className="w-5 h-5" />
                            <span>{t('eaj.lightbox.download')}</span>
                          </>
                        )}
                      </button>

                      <div className="grid grid-cols-2 gap-3">
                        <button 
                          onClick={handleShare}
                          disabled={isSharing}
                          className="flex items-center justify-center gap-2 py-3 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors"
                        >
                          {isSharing ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : showCopied ? (
                            <span className="text-green-400 text-sm">✓ {t('eaj.lightbox.copied')}</span>
                          ) : (
                            <>
                              <Share2 className="w-5 h-5" />
                              <span>{t('eaj.lightbox.share')}</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Conseils d'utilisation */}
                    <div className="text-xs text-gray-500 pt-4 border-t border-gray-800">
                      <p className="mb-1"><strong>{t('eaj.lightbox.tips')}:</strong></p>
                      <ul className="space-y-1">
                        <li>• {t('eaj.lightbox.tip1')}</li>
                        <li>• {t('eaj.lightbox.tip2')}</li>
                        <li>• {t('eaj.lightbox.tip3')}</li>
                        <li>• {t('eaj.lightbox.tip4')}</li>
                        <li>• {t('eaj.lightbox.tip5')}</li>
                      </ul>
                    </div>

                    {/* Indicateur de position */}
                    <div className="text-center text-gray-500 text-sm pt-4 border-t border-gray-800">
                      {t('eaj.lightbox.position', { 
                        current: galleryPhotos.findIndex(img => img.id === selectedImage.id) + 1,
                        total: galleryPhotos.length 
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Indicateurs de navigation */}
              <div className="flex justify-center gap-2 mt-8">
                {galleryPhotos.map((photo, index) => (
                  <button
                    key={photo.id}
                    onClick={() => {
                      setSelectedImage(photo)
                      resetZoom()
                    }}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      selectedImage.id === photo.id 
                        ? 'bg-yellow-600 w-8' 
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default EAJ